import React, { useEffect, useState } from 'react';
import { StatCard } from './charts/StatCard';
import { LineChart } from './charts/LineChart';
import { BarChart } from './charts/BarChart';
import { CostExplorerService } from '../services/CostExplorerService';
import { CostData } from '../types';
import './CostExplorer.css';

export const CostExplorer: React.FC = () => {
  const [data, setData] = useState<CostData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const service = CostExplorerService.getInstance();
      setIsConfigured(service.isConfigured());
      try {
        const costData = await service.getAllCostData();
        setData(costData);
      } catch {
        // Will use mock data
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="admin-costs">
        <h2 className="admin-costs__title">Cost Explorer</h2>
        <div className="admin-costs__loading">Loading cost data...</div>
      </div>
    );
  }

  if (!data) return null;

  const dailyCostChartData = data.dailyCosts.map(d => ({
    label: d.date,
    value: d.amount,
  }));

  const serviceChartData = data.serviceBreakdown.map(s => ({
    label: s.service.replace('Amazon ', '').replace('AWS ', ''),
    value: s.amount,
    color: s.color,
  }));

  return (
    <div className="admin-costs">
      <div className="admin-costs__header">
        <h2 className="admin-costs__title">Cost Explorer</h2>
        {!isConfigured && (
          <div className="admin-costs__notice">
            ℹ️ Using demo data. Configure AWS credentials for real cost data.
          </div>
        )}
      </div>

      <div className="admin-costs__stats-grid">
        <StatCard
          icon="💵"
          label="Current Month"
          value={`$${data.totalCurrentMonth.toFixed(2)}`}
          variant="blue"
        />
        <StatCard
          icon="📊"
          label="Forecast"
          value={`$${data.forecast.toFixed(2)}`}
          variant="orange"
          trend={data.forecast > data.previousMonth ? 'up' : 'down'}
          trendValue={`vs $${data.previousMonth.toFixed(2)} last month`}
        />
        <StatCard
          icon="📅"
          label="Previous Month"
          value={`$${data.previousMonth.toFixed(2)}`}
          variant="green"
        />
        <StatCard
          icon="📈"
          label="Change"
          value={`${data.changePercent > 0 ? '+' : ''}${data.changePercent}%`}
          variant={data.changePercent > 0 ? 'red' : 'green'}
          trend={data.changePercent > 0 ? 'up' : 'down'}
          trendValue="vs last month"
        />
      </div>

      <div className="admin-costs__charts">
        <LineChart
          data={dailyCostChartData}
          title="Daily Costs (Last 30 Days)"
          yLabel="USD ($)"
        />
        <BarChart
          data={serviceChartData}
          title="Cost by AWS Service"
        />
      </div>
    </div>
  );
};
